import { FileOpener } from "@capacitor-community/file-opener";
import { Photo } from "@capacitor/camera";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { isPlatform } from "@ionic/core";
import { ViewOptions } from "../hooks/useDocumentManager";
import {
  DocumentProvider,
  WoDocumentUpload,
} from "../models/workorders/Documents";
import { ErrorCodes, ErrorInfo } from "./ErrorHelper";
import {
  ApiResponse,
  DocumentScanAndUploadResponse,
} from "../models/api/ApiResponse";
import {
  downloadDocument,
  linkDocToWorkOrder,
  uploadDocument,
  scanAndUploadDocument,
} from "../api/Documents";
import { translateMessageList } from "../api/Languages";
import { getUserSettings } from "./UserHelper";
import { getOptionByIdWithCORP } from "../api/TabwareOptions";

const DOCUMENTS_DIR = "wo_documents";

// A downloaded document is saved under a name taken from its Description (typed
// by users) or the server's Content-Disposition header. Reduce it to a plain file
// name so it can't point outside DOCUMENTS_DIR (e.g. "../../shared_prefs/...").
export const toSafeFileName = (name: string | undefined | null): string =>
  (name ?? "")
    .split(/[\\/]/)
    .pop()!
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f<>:"|?*]/g, "_")
    .replace(/^[.\s]+/, "")
    .trim();

export interface FileOptions {
  WoNumber: string;
  File: File;
  FileName: string;
  showLoadingCallback: (showYn: boolean) => void;
  uploadDoneCallback: (result: UploadResult) => void;
}

export interface PhotoOptions {
  WoNumber: string;
  Photo: Photo;
  FileName: string;
  showLoadingCallback: (showYn: boolean) => void;
  uploadDoneCallback: (result: UploadResult) => void;
}

export interface UploadResult {
  Success: boolean;
  Response: any;
}

export const upload = async (
  options: { FileName: string },
  contentType: string,
  base64String: string,
  woNumber: string
): Promise<any> => {
  let fileName = options.FileName;
  let documentStorageType = await getOptionByIdWithCORP("DocumentsStorageType");
  let documentStorageValue = documentStorageType.data.PlantValue
    ? documentStorageType.data.PlantValue
    : documentStorageType.data.DefaultValue;
  let respUpload: ApiResponse;
  let scanAndUploadResponse: DocumentScanAndUploadResponse;
  if (documentStorageValue === "Aptean Drive") {
    const scanAndUploadApiResponse: ApiResponse = await scanAndUploadDocument({
      Filename: fileName,
      ContentType: contentType,
      DocContentBase64: base64String,
    });

    if (
      scanAndUploadApiResponse.status !== 200 ||
      scanAndUploadApiResponse.isError
    ) {
      throw new ErrorInfo(
        ErrorCodes.Document_Upload_Failed,
        scanAndUploadApiResponse.data
      );
    }

    scanAndUploadResponse =
      scanAndUploadApiResponse.data as DocumentScanAndUploadResponse;
    if (
      scanAndUploadResponse.DocumentScanResult &&
      !scanAndUploadResponse.DocumentScanResult.Success
    ) {
      let errorMessage =
        scanAndUploadResponse.DocumentScanResult.MessageList[0].Text;

      throw new ErrorInfo(ErrorCodes.Document_Upload_Failed, errorMessage);
    }

    respUpload = await uploadDocument({
      Filename: fileName,
      ContentType: contentType,
      DocContentBase64: "",
      FilePath: scanAndUploadResponse.FilePath,
    });
  } else {
    respUpload = await uploadDocument({
      Filename: fileName,
      ContentType: contentType,
      DocContentBase64: base64String,
    });
  }

  if (respUpload.status !== 200 || respUpload.isError) {
    throw new ErrorInfo(ErrorCodes.Document_Upload_Failed, respUpload.data);
  }
  const docUploaded: WoDocumentUpload = respUpload.data;
  //Link the file to the work order
  const respLinkDoc2Wo: ApiResponse = await linkDocumentToWorkOrder(
    woNumber,
    docUploaded
  );

  if (respLinkDoc2Wo.status !== 200) {
    throw new ErrorInfo(ErrorCodes.Document_Upload_Failed, respLinkDoc2Wo.data);
  }
  //get translated message for file upload
  const respMsgList: ApiResponse = await translateMessageList([
    { MessageId: "DocumentUploaded", MessageParameters: [] },
  ]);

  if (respMsgList.status !== 200) {
    console.log("Invalid TranslateMessageList request: " + respMsgList.status);
    throw new ErrorInfo(
      ErrorCodes.Document_Upload_Failed,
      "Document uploaded successfully and linked to the work order."
    );
  }
  return respMsgList.data.DocumentUploaded.MessageText;
};

export const linkDocumentToWorkOrder = async (
  woNumber: string,
  document: WoDocumentUpload
) => {
  //fetch document provider data for providerId
  return await linkDocToWorkOrder({
    DocId: document.DocumentId.toString(),
    PrintWithWoFlag: false,
    WoNumber: woNumber,
    ProviderId: await getDocumentProviderId(),
  });
};

export const getDocumentProviderId = async () => {
  const userSettings = await getUserSettings();
  if (userSettings?.DocumentProviders) {
    if (userSettings.DocumentProviders) {
      const provider = userSettings.DocumentProviders.find(
        (p: DocumentProvider) => p.Name === "Aptean DMS"
      );
      if (!!provider) {
        return provider.Providerid;
      }
    }
  }
  return 0;
};

export const downloadAndView = async (options: ViewOptions) => {
  const fileResponse: ApiResponse = await downloadDocument(
    options.Document.DownloadURL!
  );

  if (!fileResponse || !fileResponse.headers) {
    return { message: "" };
  }

  if (fileResponse.status !== 200) {
    console.log("Document download failed:", {
      status: fileResponse.status,
      data: fileResponse.data,
      platform: isPlatform("hybrid") ? "mobile" : "web",
    });

    let errorMessage = "";

    // Safe type checking for includes() method
    const dataIncludesNotFound =
      typeof fileResponse.data === "string" &&
      fileResponse.data.includes("Not Found");

    if (fileResponse.status === 404 || dataIncludesNotFound) {
      errorMessage =
        "The requested document is no longer available. It may have been deleted or moved.";
    } else if (fileResponse.status === 500) {
      // Server error handling for all types of devices.
      errorMessage =
        "A server error occurred (500). The document could not be downloaded. Please try again later or contact support.";
    } else {
      if (typeof fileResponse.data === "string") {
        try {
          const parsed = JSON.parse(fileResponse.data);
          errorMessage = parsed.message || parsed.error || fileResponse.data;
        } catch {
          errorMessage = fileResponse.data;
        }
      } else {
        errorMessage = fileResponse.data || "Unknown error occurred";
      }
    }

    return {
      message: `${fileResponse.status}: ${errorMessage}`,
    };
  }

  // Only proceed with file processing if status is 200 - no need for additional check
  let fileName = options.Document.Description;

  // Get content-type from response
  let contentType = isPlatform("hybrid")
    ? fileResponse.headers["Content-Type"]
    : fileResponse.headers["content-type"];

  const contentDisposition = isPlatform("hybrid")
    ? fileResponse.headers["Content-Disposition"]
    : fileResponse.headers["content-disposition"];

  // Read filename from content-disposition
  if (contentDisposition !== undefined) {
    fileName = contentDisposition
      .split(";")[1]
      .split("=")[1]
      .replaceAll('"', "");
  }
  fileName = toSafeFileName(fileName);

  if (!fileName) {
    fileName = `${options.WoNumber}_${
      options.Document.DocumentId
    }.${getFileExtension(contentType)}`;
  } else if (fileName.lastIndexOf(".") === -1) {
    fileName = `${fileName}.${getFileExtension(contentType)}`;
  }

  // If content-type is octet-stream, change to application/[file-extn]
  if (contentType === "application/octet-stream" && !!fileName) {
    contentType = `video/${fileName.substring(fileName.lastIndexOf(".") + 1)}`;
  }

  // Ensure correct content type for images
  if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg")) {
    contentType = "image/jpeg";
  } else if (fileName.endsWith(".png")) {
    contentType = "image/png";
  }

  const base64Data = fileResponse.data;
  const binaryData = atob(base64Data);
  const byteNumbers = new Uint8Array(binaryData.length);

  for (let i = 0; i < binaryData.length; i++) {
    byteNumbers[i] = binaryData.charCodeAt(i);
  }

  const blob = new Blob([byteNumbers], { type: contentType });

  // Save file to the device to open with FileOpener
  if (isPlatform("hybrid")) {
    const base64Data = (await convertBlobToBase64(blob)) as string;
    const localPath = `${DOCUMENTS_DIR}/${fileName}`;

    const savedFile = await Filesystem.writeFile({
      data: base64Data,
      path: localPath,
      directory: Directory.Data,
      recursive: true,
    });

    await FileOpener.open({
      filePath: savedFile.uri,
      contentType: contentType,
    })
      .then(() => {
        console.log("File " + savedFile.uri + " opened");
      })
      .catch((reason) => {
        console.log("FileOpener error: " + reason);
        return reason;
      })
      .finally(() => {
        // Delete the file
        deleteFile(localPath);
      });
  } else {
    const fileURL = URL.createObjectURL(blob);
    const canPreview =
      contentType.startsWith("image/") || contentType === "application/pdf";
    if (!canPreview) {
      const anchor = document.createElement("a");
      anchor.href = fileURL;
      anchor.download = fileName; // Set the correct file name
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(fileURL);
    }

    return { fileURL, contentType, message: "", canPreview, fileName };
  }

  return { message: "" };
};

export const readDataAsBase64 = async (photo: Photo) => {
  if (isPlatform("hybrid")) {
    const file = await Filesystem.readFile({
      path: photo.path!,
    });
    // Filesystem 5+ types data as string | Blob; native always returns base64 when no encoding is passed
    return file.data as string;
  } else {
    const resp = await fetch(photo.webPath!);
    const blob = await resp.blob();

    return (await convertBlobToBase64(blob)) as string;
  }
};

export const convertBlobToBase64 = (blob: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.readAsDataURL(blob);
  });

const deleteFile = async (filePath: string) => {
  //delete downloaded files asynchronously
  //wait 30s to avoid issue in opening the file in slow devices
  setTimeout(async () => {
    try {
      Filesystem.deleteFile({
        path: filePath,
        directory: Directory.Data,
      });
    } catch (error) {
      console.log(`Error deleting file ${filePath}: ${error}`);
    }
  }, 30000);
};

const getFileExtension = (contentType: string) => {
  const type = contentType.substring(contentType.lastIndexOf("/") + 1);
  switch (type) {
    case "vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "xlsx";
    case "vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "docx";
    case "vnd.openxmlformats-officedocument.presentationml.presentation":
      return "pptx";
    case "msword":
      return "doc";
    case "vnd.ms-excel":
      return "doc";
    case "vnd.ms-powerpoint":
      return "ppt";
    case "quicktime":
      return "mov";
  }
  if (type) {
    return type;
  }
  return "txt";
};
